import { TouchEvent, useCallback, useRef, useState } from 'react';

interface SwipeHandlers {
	onSwipeLeft?: () => void;
	onSwipeRight?: () => void;
}

interface TouchPosition {
	x: number;
	y: number;
}

export const useSwipe = (handlers: SwipeHandlers, minSwipeDistance = 50) => {
	const touchStart = useRef<TouchPosition | null>(null);
	const touchEnd = useRef<TouchPosition | null>(null);
	const elementRef = useRef<HTMLElement | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [translateX, setTranslateX] = useState(0);

	const applyTransform = useCallback((x: number, withTransition = false) => {
		if (elementRef.current) {
			elementRef.current.style.transform = `translateX(${x}px)`;
			elementRef.current.style.transition = withTransition
				? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
				: 'none';
		}
	}, []);

	const resetPosition = useCallback(() => {
		applyTransform(0, true);
		setTranslateX(0);
		setTimeout(() => setIsDragging(false), 300);
	}, [applyTransform]);

	const completeSwipe = useCallback(
		(direction: 'left' | 'right') => {
			const targetX = direction === 'left' ? -window.innerWidth : window.innerWidth;
			applyTransform(targetX, true);

			setTimeout(() => {
				if (direction === 'left') {
					handlers.onSwipeLeft?.();
				} else {
					handlers.onSwipeRight?.();
				}

				if (elementRef.current) {
					elementRef.current.style.transition = 'none';
					elementRef.current.style.transform = 'translateX(0px)';
				}
				setTranslateX(0);
				setIsDragging(false);
			}, 300);
		},
		[handlers, applyTransform],
	);

	const onTouchStart = useCallback((e: TouchEvent) => {
		touchEnd.current = null;
		touchStart.current = {
			x: e.targetTouches[0].clientX,
			y: e.targetTouches[0].clientY,
		};
		setIsDragging(true);

		if (e.currentTarget instanceof HTMLElement) {
			elementRef.current = e.currentTarget;
		}
	}, []);

	const onTouchMove = useCallback(
		(e: TouchEvent) => {
			if (!touchStart.current || !isDragging) return;

			touchEnd.current = {
				x: e.targetTouches[0].clientX,
				y: e.targetTouches[0].clientY,
			};

			const distanceX = touchStart.current.x - touchEnd.current.x;
			const distanceY = touchStart.current.y - touchEnd.current.y;
			const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);

			if (isHorizontalSwipe) {
				e.preventDefault();

				let newTranslateX = -distanceX;
				const maxTranslate = 150;
				const resistance = 0.4;

				if (Math.abs(newTranslateX) > maxTranslate) {
					const excess = Math.abs(newTranslateX) - maxTranslate;
					newTranslateX = newTranslateX > 0 ? maxTranslate + excess * resistance : -maxTranslate - excess * resistance;
				}

				setTranslateX(newTranslateX);
				applyTransform(newTranslateX);
			}
		},
		[isDragging, applyTransform],
	);

	const onTouchEnd = useCallback(() => {
		if (!touchStart.current || !touchEnd.current || !isDragging) return;

		const distanceX = touchStart.current.x - touchEnd.current.x;
		const distanceY = touchStart.current.y - touchEnd.current.y;
		const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);

		if (isHorizontalSwipe && Math.abs(distanceX) > minSwipeDistance) {
			if (distanceX > 0) {
				completeSwipe('left');
			} else {
				completeSwipe('right');
			}
		} else {
			resetPosition();
		}
	}, [isDragging, minSwipeDistance, completeSwipe, resetPosition]);

	return {
		onTouchStart,
		onTouchMove,
		onTouchEnd,
		isDragging,
		translateX,
	};
};
